// Reference ranges for the 5 tracked lab markers.
// Sources: general adult ranges drawn from NAMS position statements, ACOG
// guidance, and LabCorp standard reference intervals. Dr. Watson refines
// these in Week 5.

export type MarkerKey = "estradiol" | "fsh" | "lh" | "progesterone" | "tsh";

export type LifeStage =
  | "Perimenopause"
  | "Late perimenopause"
  | "Menopause"
  | "Postmenopause"
  | "Surgical menopause"
  | "Exploring"
  | undefined;

export type RangeResult = {
  low: number | null;
  high: number | null;
  displayContext: string;
};

export const LAB_MARKERS: ReadonlyArray<{
  key: MarkerKey;
  displayName: string;
  unit: string;
  description: string;
}> = [
  { key: "estradiol",    displayName: "Estradiol",    unit: "pg/mL",   description: "Primary estrogen" },
  { key: "fsh",          displayName: "FSH",          unit: "mIU/mL",  description: "Follicle stimulating hormone" },
  { key: "lh",           displayName: "LH",           unit: "mIU/mL",  description: "Luteinizing hormone" },
  { key: "progesterone", displayName: "Progesterone", unit: "ng/mL",   description: "Cycle and HRT marker" },
  { key: "tsh",          displayName: "TSH",          unit: "mIU/L",   description: "Thyroid screening" },
];

const PRE_STAGES = new Set(["Perimenopause", "Late perimenopause", "Exploring"]);
const POST_STAGES = new Set(["Menopause", "Postmenopause", "Surgical menopause"]);

function isTransdermal(hrtType?: string): boolean {
  if (!hrtType) return false;
  const t = hrtType.toLowerCase();
  return t.includes("transdermal") || t.includes("patch") || t.includes("gel");
}

function isOral(hrtType?: string): boolean {
  if (!hrtType) return false;
  return hrtType.toLowerCase().includes("oral");
}

export function getRange(
  marker: MarkerKey,
  stage: LifeStage,
  onHRT: boolean,
  hrtType: string | undefined,
): RangeResult {
  const pre = stage ? PRE_STAGES.has(stage) : true;
  const post = stage ? POST_STAGES.has(stage) : false;

  switch (marker) {
    case "estradiol": {
      // On-HRT overrides stage. NAMS target serum estradiol guidance.
      if (onHRT && isTransdermal(hrtType)) {
        return { low: 50, high: 150, displayContext: "On HRT, transdermal" };
      }
      if (onHRT && isOral(hrtType)) {
        return { low: 40, high: 100, displayContext: "On HRT, oral" };
      }
      if (onHRT) {
        // Unknown route — default to transdermal band (most common).
        return { low: 50, high: 150, displayContext: "On HRT, transdermal" };
      }
      if (post) {
        return { low: null, high: 30, displayContext: "Postmenopausal untreated range — low is expected" };
      }
      return { low: 30, high: 400, displayContext: "Premenopausal range — varies through cycle" };
    }
    case "fsh": {
      // ACOG / LabCorp adult ranges.
      if (post) {
        return { low: 25, high: null, displayContext: "Postmenopausal range — elevated is expected" };
      }
      return { low: 5, high: 25, displayContext: "Premenopausal range" };
    }
    case "lh": {
      if (post) {
        return { low: 15, high: 60, displayContext: "Postmenopausal range" };
      }
      return { low: 5, high: 25, displayContext: "Premenopausal range" };
    }
    case "progesterone": {
      if (post) {
        return { low: null, high: 1, displayContext: "Postmenopausal range" };
      }
      return { low: 5, high: 20, displayContext: "Premenopausal range — luteal phase reference" };
    }
    case "tsh": {
      // LabCorp standard adult range; stage-independent.
      return { low: 0.4, high: 4.0, displayContext: "Adult reference range" };
    }
  }
}

export const DISCLAIMER =
  "Reference ranges shown are general adult ranges and may differ from your lab's. Not a diagnosis. Discuss results with your clinician.";
